/**
 * Study materials are stored as a flat list of paths ("week-1/notes.pdf");
 * these helpers build the folder tree the UI shows.
 */

/** { name, path, folders: [node], files: [file], fileCount, size } */
export function buildTree(files) {
  const root = { name: "", path: "", folders: [], files: [], fileCount: 0, size: 0 };
  const index = { "": root };
  for (const file of files) {
    const parts = file.path.split("/");
    let node = root;
    for (let i = 0; i < parts.length - 1; i += 1) {
      const path = parts.slice(0, i + 1).join("/");
      if (!index[path]) {
        index[path] = { name: parts[i], path, folders: [], files: [], fileCount: 0, size: 0 };
        node.folders.push(index[path]);
      }
      node = index[path];
    }
    node.files.push({ ...file, name: parts[parts.length - 1] });
  }
  const finish = (node) => {
    node.folders.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    node.files.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    node.folders.forEach(finish);
    node.fileCount = node.files.length + node.folders.reduce((n, f) => n + f.fileCount, 0);
    node.size = node.files.reduce((n, f) => n + (f.size_bytes || 0), 0) + node.folders.reduce((n, f) => n + f.size, 0);
  };
  finish(root);
  return root;
}

/** Every file id under a folder node. */
export function idsUnder(node) {
  return [...node.files.map((f) => f.id), ...node.folders.flatMap(idsUnder)];
}

/** Files whose path or folder matches the search, as a flat list. */
export function searchFiles(files, query) {
  const q = query.trim().toLowerCase();
  return q ? files.filter((f) => f.path.toLowerCase().includes(q)) : files;
}

/** "a\\ b//./c.pdf" -> "a b/c.pdf", the way the server stores paths. */
export const normalizePath = (path) =>
  path.replace(/\\/g, "/").split("/").map((p) => p.trim()).filter((p) => p && p !== ".").join("/");

/** OS clutter that comes with folders and zips (the server skips it too). */
export const isJunk = (path) => {
  const parts = path.split("/");
  const name = parts[parts.length - 1];
  return parts.includes("__MACOSX") || name.startsWith("._") || [".DS_Store", "Thumbs.db", "desktop.ini"].includes(name);
};

/** The path a dropped/picked File should get: its folder path when it has one. */
export const relativePath = (file) => file.relativePath || file.webkitRelativePath || file.name;

const ZIP_TYPES = ["application/zip", "application/x-zip-compressed", "application/x-zip"];
export const isZip = (file) => ZIP_TYPES.includes(file.type) || /\.zip$/i.test(file.name);
